import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "http://localhost:8080";

const BASE_URL = "https://www.clearandclean.co.nz";

const EMPTY = {
  title: "", slug: "", meta_title: "", meta_description: "",
  keywords: "", h1: "", content: "", canonical_url: "", is_active: 1,
};

const SeoPages = () => {
  const [pages, setPages] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem("admin_token");
  const authHeader = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const flash = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "" }), 3000);
  };

  const loadPages = async () => {
    const res = await fetch(`${API}/?url=seo/pages&_t=${Date.now()}`);
    if (res.status === 401) { localStorage.removeItem("admin_token"); navigate("/login"); return; }
    setPages(await res.json());
  };

  useEffect(() => { loadPages(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const buildSlug = (text) =>
    text.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/[\s-]+/g, "-").trim();

  // Auto-generate slug + canonical from title when creating new
  const handleTitleChange = (e) => {
    const title = e.target.value;
    if (!editId) {
      const slug = buildSlug(title);
      setForm((f) => ({ ...f, title, slug, meta_title: title, h1: title, canonical_url: `${BASE_URL}/${slug}` }));
    } else {
      setForm((f) => ({ ...f, title }));
    }
  };

  // Keep canonical_url in sync when slug is edited manually
  const handleSlugChange = (e) => {
    const slug = buildSlug(e.target.value);
    setForm((f) => ({ ...f, slug, canonical_url: `${BASE_URL}/${slug}` }));
  };

  const openCreate = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (page) => {
    setForm({
      ...page,
      canonical_url: page.canonical_url || `${BASE_URL}/${page.slug}`,
    });
    setEditId(page.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const isEdit = !!editId;
      const url = isEdit ? `${API}/?url=seo/pages/${editId}` : `${API}/?url=seo/pages`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, { method, headers: authHeader, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) {
        flash(isEdit ? "Page updated!" : "Page created!");
        setShowForm(false);
        setEditId(null);
        loadPages();
      } else {
        flash(data.error || "Something went wrong", "danger");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await fetch(`${API}/?url=seo/pages/${id}`, { method: "DELETE", headers: authHeader });
    flash("Page deleted");
    loadPages();
  };

  const toggleActive = async (page) => {
    await fetch(`${API}/?url=seo/pages/${page.id}`, {
      method: "PUT",
      headers: authHeader,
      body: JSON.stringify({ ...page, is_active: page.is_active ? 0 : 1 }),
    });
    loadPages();
  };

  return (
    <div>
      {msg.text && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-semibold mb-0">SEO Pages</h5>
        <button className="btn btn-dark btn-sm" onClick={openCreate}>+ New Page</button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <h6 className="fw-semibold mb-3">{editId ? "Edit Page" : "Create New SEO Page"}</h6>
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Page Title *</label>
                  <input name="title" className="form-control form-control-sm" value={form.title} onChange={handleTitleChange} required />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Slug (URL)</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text text-muted">/</span>
                    <input name="slug" className="form-control" value={form.slug} onChange={handleSlugChange} required />
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">Meta Title</label>
                  <input name="meta_title" className="form-control form-control-sm" value={form.meta_title} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">H1 Heading</label>
                  <input name="h1" className="form-control form-control-sm" value={form.h1} onChange={handleChange} />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Meta Description</label>
                  <textarea name="meta_description" className="form-control form-control-sm" rows={2} value={form.meta_description} onChange={handleChange} />
                  <small className="text-muted">{form.meta_description.length}/160 chars</small>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Keywords</label>
                  <input name="keywords" className="form-control form-control-sm" value={form.keywords} onChange={handleChange} placeholder="e.g. commercial cleaning Christchurch, office cleaning NZ" />
                  <small className="text-muted">Comma-separated keywords</small>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Page Content (HTML allowed)</label>
                  <textarea name="content" className="form-control form-control-sm" rows={6} value={form.content} onChange={handleChange} />
                </div>
                <div className="col-md-8">
                  <label className="form-label small fw-semibold">Canonical URL</label>
                  <input name="canonical_url" className="form-control form-control-sm" value={form.canonical_url} onChange={handleChange} placeholder={`${BASE_URL}/your-slug`} />
                  <small className="text-muted">Auto-filled from slug. Edit only if different.</small>
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Status</label>
                  <select name="is_active" className="form-select form-select-sm" value={form.is_active} onChange={handleChange}>
                    <option value={1}>Active</option>
                    <option value={0}>Draft</option>
                  </select>
                </div>
              </div>
              <div className="d-flex gap-2 mt-3">
                <button type="submit" className="btn btn-dark btn-sm" disabled={loading}>
                  {loading ? "Saving…" : editId ? "Update Page" : "Create Page"}
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pages table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {pages.length === 0 ? (
            <p className="text-muted p-3 mb-0">No SEO pages yet. Create your first one!</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Title</th>
                    <th>Slug</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((p) => (
                    <tr key={p.id}>
                      <td className="fw-semibold">{p.title}</td>
                      <td>
                        <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="text-decoration-none text-primary small">
                          /{p.slug}
                        </a>
                      </td>
                      <td>
                        <span
                          className={`badge ${p.is_active ? "bg-success" : "bg-secondary"} cursor-pointer`}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleActive(p)}
                          title="Click to toggle"
                        >
                          {p.is_active ? "Active" : "Draft"}
                        </span>
                      </td>
                      <td className="text-muted small">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td>
                        <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => openEdit(p)}>Edit</button>
                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(p.id, p.title)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeoPages;
